/**
 * How the draft layer decides that something changed (TM-9455 / ADR-0029 §D, §H-1).
 *
 * Dirtiness anywhere in the draft layer is a live comparison of the current values
 * against a movable BASELINE — never a persistent touched flag. Typing a character and
 * deleting it makes an Entwurf clean again, deliberately: an empty form is not work
 * worth pinning to the rail, and the alternative means one accidental keystroke pins an
 * Entwurf until somebody explicitly discards it.
 *
 * The algorithm lives here because it had two implementations — the buffer store's, for
 * a draft the panel may no longer be mounted for, and `useFormTouched`'s, for a form
 * that is not a draft at all. They were the same two lines, and two copies of a
 * comparison is two answers to "did this change?" waiting to diverge.
 */

/**
 * The value as it will be compared later.
 *
 * JSON, for two reasons that are both load-bearing. It reads THROUGH Vue's proxies, so
 * a computed built on top of this tracks every key it touched and re-runs when any of
 * them changes — an identity or key-count check would not. And it ignores object
 * identity, so a picker that rebuilds an equal array, or a `ref()` that wraps a value
 * in a fresh proxy, does not read as a change; comparing by identity reported every
 * form as permanently dirty.
 *
 * The cost is that a value carrying no JSON of its own flattens: `JSON.stringify` of a
 * `File` is `{}` in a browser, so every picked image looks alike. That is what a panel's
 * projection is for — see `DraftFormOptions.project`. Note that a unit test cannot show
 * this: the test environment's `File` serialises its `name`, `type` and `lastModified`.
 *
 * A baseline is always compared SNAPSHOT against SNAPSHOT, never value against snapshot:
 * the baseline is a string held across a remount, and the object it was taken from is
 * long gone by the time anything asks.
 */
export function valueSnapshot(value: unknown): string {
  return JSON.stringify(value);
}
